import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { getMyWorkspaces } from '@/features/onboarding/services/workspaceApi'
import * as notificationsApi from '@/features/hubspot/services/notificationsApi'
import type { NotificationRecord } from '@/features/hubspot/services/notificationsApi'
import { usePushNotifications } from '@/features/settings/hooks/usePushNotifications'

const POLL_INTERVAL_MS = 5_000

// Module-level, not per-provider-instance state: NotificationsProvider is
// remounted on every /dashboard, /dashboard/chat, /dashboard/settings
// navigation (see App.tsx — each route wraps its own instance), so a
// component-local guard would re-prompt on every navigation while the
// browser permission stays 'default' (dismissed-without-deciding). This
// resets only on a hard page reload, which is the right granularity: ask
// once per tab session, try again next time the app is opened fresh.
let hasPromptedForPushThisSession = false

interface NotificationsContextValue {
  notifications: NotificationRecord[]
  workspaceId: string | null
  dismiss: (id: string) => void
}

const NotificationsContext = createContext<NotificationsContextValue>({
  notifications: [],
  workspaceId: null,
  dismiss: () => {},
})

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const [workspaceId, setWorkspaceId] = useState<string | null>(null)
  const [notifications, setNotifications] = useState<NotificationRecord[]>([])
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const push = usePushNotifications()

  // Resolve workspaceId once on mount
  useEffect(() => {
    getMyWorkspaces()
      .then((ws) => { if (ws.length > 0) setWorkspaceId(ws[0].id) })
      .catch(() => {})
  }, [])

  // Ask for browser-notification permission right when the (logged-in) app
  // starts, rather than waiting for the person to find the Settings toggle.
  // Only fires while permission is still 'default' — a prior grant or an
  // explicit block is never re-prompted (the browser wouldn't show the
  // prompt again anyway; this just avoids calling enable() pointlessly).
  useEffect(() => {
    if (hasPromptedForPushThisSession) return
    if (push.loading || !push.supported || push.permission !== 'default') return
    hasPromptedForPushThisSession = true
    void push.enable()
    // Deliberately omits `push` from deps — enable/loading/permission are
    // recreated each render (usePushNotifications isn't memoized across
    // instances), which would otherwise refire this on unrelated re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [push.loading, push.supported, push.permission])

  const fetchNotifications = useCallback(async () => {
    if (!workspaceId) return
    try {
      const res = await notificationsApi.listNotifications(workspaceId)
      setNotifications(res.data ?? [])
    } catch (err) {
      console.error('[NotificationsContext] fetch failed:', err)
    }
  }, [workspaceId])

  // Start polling when workspaceId is available
  useEffect(() => {
    if (!workspaceId) return
    fetchNotifications()
    pollRef.current = setInterval(fetchNotifications, POLL_INTERVAL_MS)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [workspaceId, fetchNotifications])

  const dismiss = useCallback(async (id: string) => {
    if (!workspaceId) return
    // Optimistic removal
    setNotifications((prev) => prev.filter((n) => n.id !== id))
    try {
      await notificationsApi.dismissNotification(workspaceId, id)
    } catch {
      fetchNotifications() // Re-fetch to restore on error
    }
  }, [workspaceId, fetchNotifications])

  return (
    <NotificationsContext.Provider value={{ notifications, workspaceId, dismiss }}>
      {children}
    </NotificationsContext.Provider>
  )
}

export function useNotificationsContext() {
  return useContext(NotificationsContext)
}
